import React from 'react';
import { getInputTypeForFieldType } from '../../core/utilities/gridQuestionUtils';
import SearchableSelect from '../SearchableSelect/SearchableSelect';

const parseLayout = (layoutJson) => {
	try {
		const layout = JSON.parse(layoutJson || '{}') || {};
		return {
			columns: layout.columns || [],
			columnGroups: layout.columnGroups || [],
			rows: layout.rows || [],
			rowGroups: layout.rowGroups || [],
		};
	} catch {
		return { columns: [], columnGroups: [], rows: [], rowGroups: [] };
	}
};

// Walks an ordered key list (column or row keys) and the span-group overlay
// that references it, producing one segment per rendered header cell: either
// a labeled group spanning however many keys it covers, or a single
// unlabeled key. Groups are expected to cover a contiguous run of keys in
// order (that's how the editor creates them); a key already claimed by an
// earlier segment is skipped so overlapping groups can't double-render.
const buildHeaderSegments = (keys, groups) => {
	const segments = [];
	const claimed = new Set();
	keys.forEach((key) => {
		if (claimed.has(key)) return;
		const group = groups.find((g) => g.colKeys ? g.colKeys[0] === key : g.rowKeys[0] === key);
		const groupKeys = group ? (group.colKeys || group.rowKeys) : null;
		if (group && groupKeys.every((k) => keys.includes(k))) {
			groupKeys.forEach((k) => claimed.add(k));
			segments.push({ key: group.key, label: group.label, span: groupKeys.length });
		} else {
			claimed.add(key);
			segments.push({ key, label: '', span: 1 });
		}
	});
	return segments;
};

// Renders a GRID question's layout as a table. Three mutually exclusive
// rendering modes, driven by which props are passed:
//
// - `editable` (used by GridQuestionEditor): a searchable linkable-field dropdown plus
//   "Hide elsewhere" checkbox per cell, for configuring cell links.
// - `answerContext` with `readOnly: false` (used by the fill-out flow, e.g.
//   FormSubmit): a real input per linked cell, wired to that cell's linked
//   field's own answer via `getDisplayValue`/`getChangeHandler` — the grid
//   itself never owns an answer, it just points at the linked field's.
// - `answerContext` with `readOnly: true` (used by SubmittedResponseView):
//   the linked field's already-formatted answer text/node per cell.
// - neither prop (builder preview elsewhere, e.g. FormPreview): a disabled
//   placeholder input or a dash for unlinked cells.
const GridQuestionView = ({
	content, editable = false, linkableFields = [], linkedElsewhere, onCellLinkChange, answerContext,
}) => {
	const { columns, columnGroups, rows, rowGroups } = parseLayout(content?.gridQuestionConfig?.layoutJson);
	const cellLinksMap = (content?.gridQuestionConfig?.cellLinks || []).reduce((acc, link) => {
		acc[link.cellKey] = link;
		return acc;
	}, {});

	if (columns.length === 0 || rows.length === 0) {
		return <div className="GridQuestionView text-muted">Grid (no layout configured)</div>;
	}

	const columnHeaderSegments = buildHeaderSegments(columns.map((c) => c.key), columnGroups);
	const hasColumnGroupLabels = columnHeaderSegments.some((seg) => seg.span > 1);
	const rowHeaderSegments = buildHeaderSegments(rows.map((r) => r.key), rowGroups);
	const rowsByKey = rows.reduce((acc, row) => { acc[row.key] = row; return acc; }, {});

	const renderCell = (row, col) => {
		const cellKey = `${row.key}_${col.key}`;
		const link = cellLinksMap[cellKey];
		if (editable) {
			const excluded = linkedElsewhere ? linkedElsewhere(cellKey) : new Set();
			return (
				<td key={col.key} className="GridQuestionView__cell">
					<SearchableSelect
						id={`grid-cell-link-${cellKey}`}
						placeholder="Not linked"
						options={linkableFields
							.filter((f) => f.id === link?.linkedFieldId || !excluded.has(f.id))
							.map((f) => ({ value: f.id, label: f.name }))}
						value={link?.linkedFieldId || ''}
						onChange={(value) => onCellLinkChange(cellKey, {
							linkedFieldId: value ? +value : null,
							linkedFieldType: linkableFields.find((f) => f.id === +value)?.type,
						})}
					/>
					<label className="d-flex align-items-center gap-1 mb-0 text-nowrap small">
						<input
							type="checkbox"
							checked={link?.hideLinkedField !== false}
							disabled={!link?.linkedFieldId}
							onChange={(e) => onCellLinkChange(cellKey, { hideLinkedField: e.target.checked })}
						/>
						Hide elsewhere
					</label>
				</td>
			);
		}
		if (answerContext) {
			if (!link?.linkedFieldId) {
				return <td key={col.key}><span className="text-muted">—</span></td>;
			}
			const displayValue = answerContext.getDisplayValue(link.linkedFieldId);
			if (answerContext.readOnly) {
				return (
					<td key={col.key}>
						{displayValue !== '' && displayValue != null ? displayValue : <span className="text-muted">—</span>}
					</td>
				);
			}
			const onChange = answerContext.getChangeHandler(link.linkedFieldId);
			const extra = answerContext.renderExtra?.(link.linkedFieldId);
			return (
				<td key={col.key} className="GridQuestionView__cell">
					<div className={extra ? 'd-flex align-items-center gap-1' : undefined}>
						<input
							className="form-control"
							type={answerContext.getInputType ? answerContext.getInputType(link.linkedFieldId) : 'text'}
							value={displayValue ?? ''}
							onChange={onChange}
							disabled={!onChange}
						/>
						{extra}
					</div>
				</td>
			);
		}
		return (
			<td key={col.key}>
				{link?.linkedFieldId ? (
					<input className="form-control" type={getInputTypeForFieldType(link.linkedFieldType)} disabled />
				) : (
					<span className="text-muted">—</span>
				)}
			</td>
		);
	};

	return (
		<div className="GridQuestionView table-responsive">
			<table className="table table-bordered mb-2">
				<thead>
					{hasColumnGroupLabels && (
						<tr>
							<th />
							{columnHeaderSegments.map((seg) => <th key={seg.key} colSpan={seg.span}>{seg.label}</th>)}
						</tr>
					)}
					<tr>
						<th />
						{columns.map((col) => <th key={col.key}>{col.label}</th>)}
					</tr>
				</thead>
				<tbody>
					{rowHeaderSegments.map((seg) => {
						if (seg.span === 1) {
							const row = rowsByKey[seg.key];
							return (
								<tr key={row.key}>
									<th scope="row">{row.label}</th>
									{columns.map((col) => renderCell(row, col))}
								</tr>
							);
						}
						const rowGroup = rowGroups.find((g) => g.key === seg.key);
						return (
							<React.Fragment key={seg.key}>
								<tr className="GridQuestionView__groupRow">
									<td colSpan={columns.length + 1}><strong>{seg.label}</strong></td>
								</tr>
								{rowGroup.rowKeys.map((rowKey) => {
									const row = rowsByKey[rowKey];
									return (
										<tr key={row.key}>
											<th scope="row">{row.label}</th>
											{columns.map((col) => renderCell(row, col))}
										</tr>
									);
								})}
							</React.Fragment>
						);
					})}
				</tbody>
			</table>
		</div>
	);
};

export default GridQuestionView;
